/**
 * Value + rule -> editor control (design C.3).
 *
 * This is the file that makes the page GENERIC rather than a ninth hand-written
 * mirror of a DTO. The three review pages it replaces each hardcoded the field
 * list they rendered, so a key nobody typed into that list had no editor — and
 * because `PUT /{x}/enrich` full-replaces the stored enrichment, "no editor"
 * meant "destroyed on the next approval". Resolving a control from the runtime
 * value means an unmodelled key still renders, and still round-trips.
 *
 * The key lists below are the exception to that genericity, and they are
 * deliberate: they name the keys that must NOT get an editor.
 */
import type { EnrichmentControl, EnrichmentFieldRule } from "./config";

/**
 * Thrown when a value cannot be turned into a control, or a resource has no
 * config. A thrown error is right here — the alternative is rendering an empty
 * control over a key the enrich PUT will then replace with nothing.
 */
export class EnrichmentSchemaError extends Error {
  /** The field key (or resource name) that could not be modelled. */
  readonly key: string;

  constructor(key: string, message: string) {
    super(message);
    this.name = "EnrichmentSchemaError";
    this.key = key;
  }
}

/** Owned by their own sections — never rendered as an inline field. */
export const NEVER_INLINE_KEY_LIST = ["translations", "transliterations"];
export const NEVER_INLINE_KEYS: ReadonlySet<string> = new Set(
  NEVER_INLINE_KEY_LIST,
);

/**
 * Pipeline-owned: written by the enricher or by Firestore, and carried through
 * the round trip untouched.
 *
 * `id` is on the list although every handler overwrites it: the DTO marks it
 * `required`, so a missing one is a 400 before the handler runs.
 */
export const PIPELINE_OWNED_KEY_LIST = [
  "id",
  "taskName",
  "enrichmentSources",
  "componentEnrichments",
  "createdAt",
  "createdBy",
  "updatedAt",
  "updatedBy",
];
export const PIPELINE_OWNED_KEYS: ReadonlySet<string> = new Set(
  PIPELINE_OWNED_KEY_LIST,
);

/**
 * Read-only by owner decision D1: `reasoning` is shown in a collapsible card,
 * never as an input. A curator cannot improve a model's rationale.
 */
export const READ_ONLY_KEY_LIST = ["reasoning"];
export const READ_ONLY_KEYS: ReadonlySet<string> = new Set(READ_ONLY_KEY_LIST);

/**
 * Entity-level keys that must never be mistaken for enrichment members when the
 * whole document is the enrichment object (genre's flat shape).
 */
export const DOCUMENT_ONLY_KEYS: ReadonlySet<string> = new Set([
  ...PIPELINE_OWNED_KEY_LIST,
  "name",
  "slug",
  "status",
  "hide",
  "deprecated",
  "enrichmentStatus",
  "embeddingStatus",
  "enrichmentRetryCount",
  "embeddingRetryCount",
]);

/** Above this length a string is a paragraph, not a title. */
export const MAX_TEXT_LENGTH = 120;

/** A plain JSON object — not an array, not null. */
export function isPlainObject(
  value: unknown,
): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** A de-camel-cased key, e.g. `relatedSearches` -> "Related searches". */
export function labelForKey(key: string): string {
  const spaced = key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim();
  if (spaced.length === 0) return key;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** True when every entry is a non-null string. */
function isStringArray(value: unknown[]): value is string[] {
  return value.every((entry) => typeof entry === "string");
}

/** `{ question, answer }[]` — the FAQ member shape. */
function isFaqArray(value: unknown[]): boolean {
  return (
    value.length > 0 &&
    value.every(
      (entry) =>
        isPlainObject(entry) &&
        ("question" in entry || "answer" in entry) &&
        Object.keys(entry).every((k) => k === "question" || k === "answer"),
    )
  );
}

/** `{ id, name }[]` — the genre-reference member shape. */
function isGenreRefArray(value: unknown[]): boolean {
  return (
    value.length > 0 &&
    value.every(
      (entry) =>
        isPlainObject(entry) &&
        ("id" in entry || "name" in entry) &&
        Object.keys(entry).every((k) => k === "id" || k === "name"),
    )
  );
}

/** Every member is a number (or null/undefined) — a feature score map. */
function isNumericMap(value: Record<string, unknown>): boolean {
  const values = Object.values(value);
  return (
    values.length > 0 &&
    values.every(
      (entry) =>
        entry === null || entry === undefined || typeof entry === "number",
    )
  );
}

/**
 * The control for a value the config does not name.
 *
 * Throws for `null`/`undefined`: there is no type to derive a control from, and
 * guessing one is exactly how a key ends up rendered as a text box over an
 * object and then written back as a string.
 */
export function deriveControl(key: string, value: unknown): EnrichmentControl {
  if (value === null || value === undefined) {
    throw new EnrichmentSchemaError(
      key,
      `cannot model "${key}": the value is ${
        value === null ? "null" : "absent"
      } and no config rule names it`,
    );
  }
  if (typeof value === "string") {
    return value.length > MAX_TEXT_LENGTH ? "textarea" : "text";
  }
  if (Array.isArray(value)) {
    if (value.length === 0 || isStringArray(value)) return "stringList";
    if (isFaqArray(value)) return "faq";
    if (isGenreRefArray(value)) return "genreRefs";
    return "json";
  }
  if (typeof value === "boolean") return "boolean";
  if (typeof value === "number") return "text";
  if (isPlainObject(value)) {
    // The one key-name special case, and a safety net only: every entity that
    // HAS features names them in its config, so this fires when a config
    // forgets. Recursing into 23 numeric members as `object` would be worse.
    if (key === "features" && isNumericMap(value)) return "features";
    return "object";
  }
  return "json";
}

/**
 * Resolve one control, in the order design C.3 fixes:
 *
 * 1. never-inline (`translations`, `transliterations`) -> hidden;
 * 2. pipeline-owned and system keys -> hidden;
 * 3. `reasoning` -> hidden (D1);
 * 4. the config's own rule, when it names the key;
 * 5. otherwise derive from the runtime value.
 */
export function controlFor(
  key: string,
  value: unknown,
  rule?: EnrichmentFieldRule,
): EnrichmentControl {
  if (NEVER_INLINE_KEYS.has(key)) return "hidden";
  if (PIPELINE_OWNED_KEYS.has(key)) return "hidden";
  if (READ_ONLY_KEYS.has(key)) return "hidden";
  if (rule) return rule.control;
  return deriveControl(key, value);
}

/** Controls whose value the curator can type: the `""`-clear allowlist. */
export function isClearableControl(control: EnrichmentControl): boolean {
  return control === "text" || control === "textarea" || control === "area";
}

/** Every key of a config whose control is a text field (design C.6). */
export function clearableKeysFor(config: {
  fields: EnrichmentFieldRule[];
}): string[] {
  return config.fields
    .filter((rule) => rule.clearable ?? isClearableControl(rule.control))
    .map((rule) => rule.key);
}

/** The empty value a required member is written with when nothing was stored. */
export function emptyValueForControl(
  control: EnrichmentControl,
): unknown | undefined {
  switch (control) {
    case "text":
    case "textarea":
    case "area":
      return "";
    case "stringList":
    case "faq":
    case "genreRefs":
      return [];
    case "features":
      return {};
    default:
      return undefined;
  }
}
